/**
 * tourSteps — the first-run guided walkthrough script.
 *
 * The tour is a spotlight tour: most steps NAVIGATE to a real route and dim
 * everything except one real element (looked up by its `data-testid`), so the
 * trainer learns the actual UI rather than a set of drawn mock-ups. Steps that
 * cannot be driven live — starting a system broadcast, parking the iOS PiP
 * window over Pokémon GO, uploading a screenshot — are `card` steps: the same
 * chrome, centred, with no spotlight.
 *
 * Data lives here (pure, no React) so the script can be unit-tested and so the
 * ordering is readable in one place. The engine is GuidedTour.tsx.
 *
 * Contract with the engine:
 *   - `route` is a hash route ("/", "/dex", "/settings"). The engine navigates
 *     there before it looks for the target, and waits for the element.
 *   - `target` is a `data-testid` value. If the element never appears (a Pro
 *     surface, an empty dex, a device without the control) the engine degrades
 *     to a centred card rather than stalling or blocking the tour. Every
 *     spotlight step must therefore read correctly with no spotlight.
 *   - Copy rules, inherited from Onboarding.tsx: no emojis, no em dashes,
 *     "Confirmed" not "Canonical", "Nex companion" not "Nex AI".
 */

export type TourStepKind = "card" | "spotlight";

export interface TourStep {
  /** Stable key: used for React keys, testids, and analytics. */
  key: string;
  kind: TourStepKind;
  /** Hash route the step lives on. Omitted means "wherever we already are". */
  route?: string;
  /** data-testid of the element to spotlight. Only meaningful for `spotlight`. */
  target?: string;
  /**
   * A full CSS selector for the spotlight, when a testid alone cannot name
   * the element: the first magnifier inside the ranked list, the first row
   * of a section. Wins over `target` when both are set (keep `target` too, so
   * the script-integrity tests still see what the step is about).
   */
  targetSelector?: string;
  /**
   * The spotlight resolved at activation from device state. "bottom-slot-tab"
   * is the trainer's chosen fourth tab (bottomSlotPref): the intro's My
   * Pokémon step describes the default, but a trainer who moved Meta or
   * Search into that slot and replays the tour still gets a ring on the tab
   * that is actually there.
   */
  targetFrom?: "bottom-slot-tab";
  /** Small uppercase label above the title. */
  eyebrow: string;
  title: string;
  body: string;
  /** Optional supporting points, rendered as a tick list. */
  bullets?: string[];
  /**
   * Pads the spotlight ring. Wide sticky bands and the tab bar look better with
   * a tighter ring than a small pill does. Defaults to 8.
   */
  padding?: number;
  /**
   * Where the engine scrolls the target within the viewport before framing it.
   * Defaults to "center". Use "start" when the target sits high on the page and
   * its own content (e.g. the selected filter pill in the variant row) would
   * otherwise be covered by the copy card: aligning it to the top of the
   * viewport lifts it clear so the card can sit below it.
   */
  scrollBlock?: "center" | "start";
  /**
   * Holds the main menu OPEN for the length of this step.
   *
   * The one thing in the app that does not exist until you tap it. A ring
   * around a closed three-line button teaches nothing, so the engine opens the
   * panel (via lib/tourMenuOpen) and the step spotlights the list itself. The
   * panel is inert while it is up: the tour's scrim sits above it, so no menu
   * row can be tapped into a state the next step does not expect.
   */
  openMenu?: boolean;
  /**
   * Per-platform copy overrides. When the running platform (iOS / Android) has
   * an entry, the listed fields REPLACE the base ones for that platform; a field
   * left out of the override keeps its base value. This lets a single step read
   * correctly on both — iOS uses ReplayKit screen broadcasting and a draggable
   * PiP HUD, Android uses MediaProjection screen recording and a fixed top HUD —
   * without forking the whole script or changing the step count / numbering. The
   * base fields are the neutral fallback (web / tests). Resolved by the engine
   * via `stepForPlatform`.
   */
  /**
   * Route resolved at activation from live data (a friend's Compare or Trade
   * page needs an id). Used by the advanced walkthroughs; `route` wins when
   * both are set. Resolves to null when there is nothing to open, and the
   * step then shows as a centred card.
   */
  routeFrom?: "friend-compare" | "friend-trades" | "first-my-pokemon";
  /**
   * A control (data-testid) the engine taps once the route is up, before it
   * looks for `target`: a pane tab, a mode switch, a filter menu toggle. How a
   * walkthrough reaches a view that is not the page's default. Pressed ONLY
   * when the target is not already showing (2026-09-19): a toggle pressed
   * again on the way back through a walk closed the very panel it opened.
   */
  press?: string;
  platform?: Partial<
    Record<"ios" | "android", Partial<Pick<TourStep, "title" | "body" | "bullets">>>
  >;
}

/**
 * The script, in the order a new trainer meets the product:
 *   - the five bottom-bar destinations (Profile, Friends, Raid, Calendar,
 *     and the raised Nexus Dex medallion in the centre)
 *   - the main menu, which is where everything the bar has no room for lives
 *   - the Dex itself: dex types, turning them on and off, manual edit,
 *     per-species detail pages, Bulk Update
 *   - Live Scan: the menu, the broadcast, the PiP HUD placement, the scroll
 *   - the screenshot upload path, and the Pro live detail-page scan
 *
 * Steps are numbered in their own eyebrows ("Bottom bar 1 of 5"), so inserting
 * a step means fixing the eyebrows in that group and nothing else.
 */
export const TOUR_STEPS: readonly TourStep[] = [
  {
    key: "welcome",
    kind: "card",
    route: "/",
    eyebrow: "Welcome",
    title: "Let's set up your Nexus Dex",
    body:
      "Let's take a quick tour of everything Nexus Dex does. You can leave at any point and pick it up again from Settings.",
    bullets: [
      "The five places the bottom bar takes you, and the menu behind them",
      "How your Dex is edited, filtered, and bulk updated",
      "How a live scan reads your Pokédex as you scroll",
    ],
  },

  /* ── The bottom bar ─────────────────────────────────────────────────── */
  {
    key: "tab-profile",
    kind: "spotlight",
    route: "/",
    target: "tab-profile",
    eyebrow: "Bottom bar 1 of 5",
    title: "Profile",
    body:
      "Your home base. Pokédex completion at a glance, what to chase next, and the fastest route back into a scan.",
    bullets: [
      "Live completion across every dex you track",
      "Up Next picks based on your priorities",
      "Your trainer card and wish list",
    ],
    padding: 4,
  },
  {
    key: "tab-friends",
    kind: "spotlight",
    route: "/friends",
    target: "tab-friends",
    eyebrow: "Bottom bar 2 of 5",
    title: "Friends",
    body:
      "Link Nexus Dex with friends you trade with, to compare side by side at a glance or possible trade opportunities.",
    bullets: [
      "Compare any friend's Dex against yours",
      "Trade matches ranked by what you are both missing",
    ],
    padding: 4,
  },
  {
    // Was the Raid tab. Raid went dormant on 2026-08-17 and Search took the slot
    // back, so this step follows the bar rather than describing a feature a new
    // trainer cannot reach — the tour would otherwise spotlight a tab that is
    // not there and sell something the menu marks Coming Soon.
    //
    // No route: the bar is on every screen, and navigating to /trade would walk
    // a brand-new trainer into the search page's Pro gate mid-tour.
    // 2026-08-19: this slot is now the TRAINER'S CHOICE; 2026-08-20 My Pokémon
    // shipped and took the default, so the step describes it and targets
    // `tab-my-pokemon`. It is deliberately NOT written to follow the setting:
    // a tour runs on first launch, before anyone has changed it, and a step
    // that reads its own target out of localStorage would be a spotlight that
    // sometimes finds nothing. Whoever adds an option to bottomSlotPref should
    // check this step still makes sense as the default's description.
    key: "tab-my-pokemon",
    kind: "spotlight",
    target: "tab-my-pokemon",
    // 2026-09-19: on replay the ring follows whatever the slot holds now.
    targetFrom: "bottom-slot-tab",
    eyebrow: "Bottom bar 3 of 5",
    title: "My Pokémon",
    body:
      "Your own Pokémon, one by one. Open one in Pokémon GO during a Live Scan and its CP, IVs and moves land here automatically.",
    bullets: [
      "IV check without leaving the game: bars, percent, and star rating",
      "PvP standing per league, plus what each one is worth keeping for",
      "Already use Poké Genie? Import its CSV in one tap",
    ],
    padding: 4,
  },
  {
    key: "tab-calendar",
    kind: "spotlight",
    route: "/calendar",
    target: "tab-calendar",
    eyebrow: "Bottom bar 4 of 5",
    title: "Calendar",
    body:
      "Every live and upcoming event, with countdowns. Mark the ones you are playing and they show up in Today's Focus.",
    bullets: [
      "Community Days, raid hours, and spotlight hours",
      "Add an event to your device calendar",
      "Pro feature: Pokédex is compared to Pokémon GO events so you can easily see what you and friends are missing and should focus on.",
    ],
    padding: 4,
  },
  {
    key: "tab-dex",
    kind: "spotlight",
    route: "/calendar",
    target: "tab-dex",
    eyebrow: "Bottom bar 5 of 5",
    title: "Nexus Dex",
    body:
      "The raised medallion in the centre is your Nexus Dex. It is the heart of the app, and the rest of this tour lives inside it.",
    bullets: [
      "Tap it to open your Nexus Dex",
      "Press and hold for a quick menu: wish list, notifications, settings, live scan",
    ],
    padding: 6,
  },

  /* ── The menu ───────────────────────────────────────────────────────── */
  {
    // The panel, not the button that opens it: `openMenu` holds the menu up for
    // this step so the spotlight can frame the actual list. A ring around a
    // closed three-line button would be a step about a button.
    key: "main-menu",
    kind: "spotlight",
    route: "/",
    target: "main-menu",
    openMenu: true,
    eyebrow: "The menu",
    title: "Everything else lives here",
    // Shortened 2026-08-17. The old body spent three sentences justifying why a
    // menu exists ("the bar has five slots and the app has more than five
    // places"), which is a thing the trainer never asked. The bullets already
    // say what is inside, so the body only has to say what the place IS and
    // give a reason to come back.
    body:
      "All the cool features are here. Check back often.",
    bullets: [
      "Search, the Pokémon GO search string builder, is in here",
      "So are Bulk Update, Wish List, Compare, Trade and Scan History",
      "Your tier sits at the top, and tapping it is how you manage it",
    ],
    padding: 6,
  },

  /* ── Inside the Dex ─────────────────────────────────────────────────── */
  {
    key: "dex-variants",
    kind: "spotlight",
    route: "/dex",
    target: "pokedex-variant-tabs",
    eyebrow: "Your Dex 1 of 5",
    title: "You have more than one Dex",
    body:
      "This row is the dex type selector, the same carousel Pokémon GO uses. Each type is a completely separate collection with its own completion.",
    bullets: [
      "Main, Shiny, Lucky, XXL, XXS, Shadow, Purified, Mega, and more",
      "Custom dexes like Shiny Perfect and Shiny Lucky",
      "Swipe the grid left or right to flip between Pokédex's",
    ],
    // The variant row sits high on the Dex page; align it to the top so the
    // selected filter pill clears the copy card below it.
    scrollBlock: "start",
  },
  {
    key: "dex-visibility",
    kind: "spotlight",
    route: "/settings",
    target: "settings-section-dex-visibility",
    eyebrow: "Your Dex 2 of 5",
    title: "Turn off the ones you don't chase",
    body:
      "Not every trainer collects every dex. Settings has a switch per dex type, so the ones you don't care about, disappear from the selector entirely.",
    bullets: [
      "Hidden dexes keep their data, they just stop taking up space",
      "The Main dex cannot be hidden",
      "Linked dexes can update each other, so a Shiny Lucky can credit both",
    ],
    padding: 6,
  },
  {
    key: "dex-manual-edit",
    kind: "spotlight",
    route: "/dex",
    target: "actionbar-manual-edit",
    eyebrow: "Your Dex 3 of 5",
    title: "Edit whenever you want",
    body:
      "Tap Manual Edit to arm the grid, then tap any entry to flip it between caught and missing. Tap the button again when you are done.",
    bullets: [
      "Works on every dex type, including the manual-only custom dexes",
      "Pro feature: Changes sync to your account and follow you to any device",
    ],
  },
  {
    key: "dex-detail",
    kind: "spotlight",
    // Show an ACTUAL species detail page behind the card (Bulbasaur #1), not the
    // middle of the dex grid, so the copy matches what the trainer sees.
    route: "/pokemon/1",
    target: "pokemon-variant-grid",
    eyebrow: "Your Dex 4 of 5",
    title: "Tap a Pokémon for its full page",
    body:
      "With Manual Edit off, tapping an entry opens that species' detail page: every dex it belongs to, every regional form, and every variant you do or do not have.",
    bullets: [
      "Shiny, Lucky, XXL, XXS, Shadow, Purified, Mega, and G-Max in one view",
      "Regional forms such as Alolan, Galarian, and Hisuian",
      "Edit any variant from there without going back to the grid",
    ],
    padding: 6,
  },
  {
    key: "dex-bulk-update",
    kind: "spotlight",
    route: "/dex",
    target: "pokedex-quick-update-open",
    eyebrow: "Your Dex 5 of 5",
    title: "Bulk Update by region",
    body:
      "Already finished Kanto's shinies? Bulk Update marks a whole region of a dex type complete in one go, instead of tapping through hundreds of entries.",
    bullets: [
      "Pick a region down the side and a dex type across the top",
      "Fills every entry in that region for that dex",
      "Caution: this menu can also be used to remove Pokédex bulk entries quickly, so proceed with caution!",
    ],
    padding: 6,
    // Framed from the top, like the variant row: centred, the button sat
    // under the copy card on a Galaxy S21+ and the ring was never seen
    // (2026-09-29 owner report). Aligned to the top and nudged clear of the
    // sticky band, it sits above the card.
    scrollBlock: "start",
  },

  /* ── Live Scan ──────────────────────────────────────────────────────── */
  {
    key: "scan-open",
    kind: "spotlight",
    route: "/dex",
    target: "actionbar-live-scan",
    eyebrow: "Live scan 1 of 4",
    title: "Now the fast way in",
    body:
      "Live Scan reads your Pokémon GO Pokédex as you scroll it, entry by entry, and fills your Dex for you. Tap here to open the scan menu.",
    bullets: [
      "It runs on your device, we never see your screen",
      "Free for everyone, forever",
      'There is a "How to" scan guide you can refer to for more information',
    ],
  },
  {
    key: "scan-broadcast",
    kind: "card",
    eyebrow: "Live scan 2 of 4",
    title: "Start the scan",
    // Base copy is the iOS flow (ReplayKit broadcast); Android differs
    // (MediaProjection screen recording) and is supplied as an override below.
    body:
      "Tap Start scan in the menu. Your device asks you to confirm screen broadcasting, which is how Nexus Dex reads the Pokédex. Choose Nexus Dex, then Start Broadcast.",
    bullets: [
      "The first run shows a system sheet, later runs start straight away",
      "Nothing is recorded and nothing leaves your device",
      "Tap the same button again to stop the scan when you are finished",
    ],
    platform: {
      android: {
        body:
          "Tap Start scan in the menu. Your device asks you to allow screen recording, which is how Nexus Dex reads the Pokédex. Choose Nexus Dex, then tap Start now.",
        bullets: [
          "The first run asks for screen-recording permission, later runs start straight away",
          "Nothing is saved and nothing leaves your device",
          "Tap the same button again to stop the scan when you are finished",
        ],
      },
    },
  },
  {
    key: "scan-pip",
    kind: "card",
    eyebrow: "Live scan 3 of 4",
    // Base copy is the iOS flow: the HUD is a draggable PiP window. On Android
    // (2026-09-22) the HUD is a small tab docked to the LEFT edge that slides
    // up and down and opens into the full card on a tap, so the "drag it into
    // the corner" instruction is replaced below.
    title: "Park the HUD",
    body:
      "Nexus Dex floats a small HUD window over Pokémon GO showing what it is reading. Drag it into the TOP RIGHT corner, over the recently registered header. That strip is not scanned, so it is safe to cover.",
    bullets: [
      "If it does not appear on its own, tap Start HUD in the scan menu",
      "The HUD shows live progress and a scroll direction arrow",
      "Keep the full four wide grid visible below it",
    ],
    platform: {
      android: {
        title: "Watch the HUD",
        body:
          "On the Pokédex, Nexus Dex docks a small HUD bubble in the TOP RIGHT corner over the Pokédex header. It shows the scroll direction arrow, and the last twelve dex numbers it read stack under it, four wide. Slide it up or down if it is in the way.",
        bullets: [
          "If it does not appear on its own, tap Start HUD in the scan menu",
          "The bubble shows the scroll direction arrow",
          "The last twelve numbers it read sit under the bubble, four wide",
        ],
      },
    },
  },
  {
    key: "scan-scroll",
    kind: "card",
    eyebrow: "Live scan 4 of 4",
    title: "Then just scroll",
    body:
      "Switch to Pokémon GO, open the Pokédex, and pick the dex filter you are scanning so the selected Pokédex pill stays visible. Scroll slowly and pause for a beat on each screen.",
    bullets: [
      "Hold still at the start, the first read happens on a still grid",
      "Slow scrolling reads cleanly, fast scrolling reads nothing",
      "Watch the HUD to see entries confirmed as you go",
    ],
  },

  /* ── Screenshots and Pro ────────────────────────────────────────────── */
  {
    key: "upload-screenshots",
    kind: "card",
    eyebrow: "Screenshots 1 of 2",
    title: "Or upload a detail page",
    body:
      "One screenshot of a single Pokémon's page in Pokémon GO updates every dex and every form for that species at once. Upload screenshots is the third item in the scan menu.",
    bullets: [
      "Screenshot from the default view, before selecting Shiny or another tab",
      "Keep the whole card grid in frame, colour means caught and faded means not",
      "Regional form tiles under the name are read too",
    ],
  },
  {
    key: "upload-pro",
    kind: "card",
    eyebrow: "Screenshots 2 of 2",
    title: "Pro reads detail pages live",
    body:
      "With Pro you skip the screenshot entirely. Leave the scan running, open a Pokémon's page in Pokémon GO, and Nexus Dex reads the whole page as you look at it.",
    bullets: [
      "No screenshots, no uploads, no leaving the game",
      "Every Pokédex is filled in for that Pokémon in one go",
    ],
  },
  {
    key: "finish",
    kind: "card",
    route: "/dex",
    eyebrow: "Done",
    title: "Welcome to Nexus Dex",
    body:
      "Start with a live scan of your Main dex, it fills the most in the least time. You can replay this walkthrough from Settings any time you need a refresher.",
  },
] as const;


/**
 * Resolve a step's copy for the running platform. Merges the step's
 * `platform[platform]` override (title / body / bullets) over its base fields;
 * `undefined` override fields keep the base. Pure, so the engine stays a thin
 * consumer and the per-platform copy is unit-testable. Web (and any platform
 * with no override) gets the base copy unchanged.
 */
export function stepForPlatform(
  step: TourStep,
  platform: "web" | "ios" | "android",
): TourStep {
  if (platform === "web") return step;
  const override = step.platform?.[platform];
  if (!override) return step;
  return {
    ...step,
    title: override.title ?? step.title,
    body: override.body ?? step.body,
    bullets: override.bullets ?? step.bullets,
  };
}
